import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import { NavLink } from "react-router-dom";
import no from "../../img/no.png";
import "./teamworkload.css";
import "../ProgressBySector/sectorprogress.css";

/* ─────────────────────────────────────────────────────────────────────────
   Tooltip displayed on hover over the progress bar (audit mode only)
───────────────────────────────────────────────────────────────────────── */
const AuditTooltip = ({ item }) => (
  <div className="audit-tooltip">
    <div className="audit-tooltip-row accepted-row">
      <span className="audit-tooltip-dot accepted-dot" />
      <span className="audit-tooltip-label">Approved</span>
      <span className="audit-tooltip-value">{Number(item?.accepted ?? 0)}</span>
    </div>
    <div className="audit-tooltip-row rejected-row">
      <span className="audit-tooltip-dot rejected-dot" />
      <span className="audit-tooltip-label">Rejected</span>
      <span className="audit-tooltip-value">{Number(item?.rejected ?? 0)}</span>
    </div>
    <div className="audit-tooltip-row pending-row">
      <span className="audit-tooltip-dot pending-dot" />
      <span className="audit-tooltip-label">Pending</span>
      <span className="audit-tooltip-value">{Number(item?.notResponded ?? 0)}</span>
    </div>
    <div className="audit-tooltip-divider" />
    <div className="audit-tooltip-row total-row">
      <span className="audit-tooltip-label">Total Assigned</span>
      <span className="audit-tooltip-value">{Number(item?.totalAssignedQuestionForAudit ?? 0)}</span>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   Single progress bar segment (clickable NavLink)
───────────────────────────────────────────────────────────────────────── */
const BarSegment = ({ widthPct, className, count, to, onClick }) => {
  if (widthPct <= 0) return null;
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={{ display: "block", width: `${widthPct}%`, padding: 0, margin: 0 }}
    >
      <div className={`bar ${className}`}>
        {widthPct > 6 && <span className="bar-label">{count}</span>}
      </div>
    </NavLink>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Renders progress bar segments for AUDIT mode
───────────────────────────────────────────────────────────────────────── */
const renderAuditBars = (item, activeLegend) => {
  const total = Number(item?.totalAssignedQuestionForAudit) || 1;
  const accepted = Number(item?.accepted ?? 0);
  const rejected = Number(item?.rejected ?? 0);
  const pending = Number(item?.notResponded ?? 0);

  const pctOf = (n) => (n / total) * 100;
  const auditPath = `/audit/${item.roleCode === "external" ? "external" : "internal"}`;

  return (
    <>
      {activeLegend.includes("Approved") && (
        <BarSegment
          widthPct={pctOf(accepted)}
          className="accepted"
          count={accepted}
          to={`${auditPath}?auditorId=${item.userId}&status=APPROVED`}
          onClick={() =>
            localStorage.setItem(
              "reportingQuestion",
              JSON.stringify(item.acceptedQuestionIds)
            )
          }
        />
      )}
      {activeLegend.includes("rejected") && (
        <BarSegment
          widthPct={pctOf(rejected)}
          className="rejected"
          count={rejected}
          to={`${auditPath}?auditorId=${item.userId}&status=REJECTED`}
          onClick={() =>
            localStorage.setItem(
              "reportingQuestion",
              JSON.stringify(item.rejectedQuestionIds)
            )
          }
        />
      )}
      {activeLegend.includes("not-responded") && (
        <BarSegment
          widthPct={pctOf(pending)}
          className="not-responded"
          count={pending}
          to={`${auditPath}?auditorId=${item.userId}&status=PENDING`}
          onClick={() =>
            localStorage.setItem(
              "reportingQuestion",
              JSON.stringify(item.auditorNotRespondedIds)
            )
          }
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Renders progress bar segments for USER (data processing) mode
───────────────────────────────────────────────────────────────────────── */
const renderUserBars = (item, activeLegend) => {
  const accepted = Number(item?.finalAssignQuesionAccpted ?? 0);
  const rejected = Number(item?.finalAssignQuesionRejected ?? 0);
  const notResponded = Number(item?.answerNotResponded ?? 0);
  const total = accepted + rejected + notResponded || 1;

  const pctOf = (n) => (n / total) * 100;

  return (
    <>
      {activeLegend.includes("Approved") && (
        <BarSegment
          widthPct={pctOf(accepted)}
          className="accepted"
          count={accepted}
          to={{ pathname: "reporting-modules/all-module", state: { reportingQuestion: item.finalAssignQuesionAccptedIds } }}
          onClick={() =>
            localStorage.setItem(
              "reportingQuestion",
              JSON.stringify(item.finalAssignQuesionAccptedIds)
            )
          }
        />
      )}
      {activeLegend.includes("rejected") && (
        <BarSegment
          widthPct={pctOf(rejected)}
          className="rejected"
          count={rejected}
          to={{ pathname: "reporting-modules/all-module", state: { reportingQuestion: item.finalAssignQuesionRejectedIds, userId: item?.userId } }}
          onClick={() =>
            localStorage.setItem(
              "reportingQuestion",
              JSON.stringify(item.finalAssignQuesionRejectedIds)
            )
          }
        />
      )}
      {activeLegend.includes("not-responded") && (
        <BarSegment
          widthPct={pctOf(notResponded)}
          className="not-responded"
          count={notResponded}
          to={{ pathname: "reporting-modules/all-module", state: { reportingQuestion: item.answerNotRespondedIds } }}
          onClick={() =>
            localStorage.setItem(
              "reportingQuestion",
              JSON.stringify(item.answerNotRespondedIds)
            )
          }
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────── */
const TeamWorkLoad = ({ user, teamWorkloadData }) => {
  const [activeLegend, setActiveLegend] = useState(["Approved", "rejected", "not-responded"]);
  const [hoveredUserId, setHoveredUserId] = useState(null);

  const handleLegendClick = (legendType) => {
    setActiveLegend((prev) =>
      prev.includes(legendType) ? prev.filter((i) => i !== legendType) : [...prev, legendType]
    );
  };

  const isUser = user === "user";

  const hasData = (item) => {
    if (isUser) {
      const total =
        Number(item?.finalAssignQuesionAccpted ?? 0) +
        Number(item?.finalAssignQuesionRejected ?? 0) +
        Number(item?.answerNotResponded ?? 0);
      return total > 0;
    }
    return Number(item?.totalAssignedQuestionForAudit ?? 0) > 0;
  };

  const filteredData = (teamWorkloadData || []).filter(hasData);

  return (
    <div className="twl-card">
      {/* Header */}
      <div className="twl-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          {/* Accent bar */}
          <div className="chart-card-accent" style={{
            width: '4px',
            height: '36px',
            borderRadius: '3px',
            flexShrink: 0,
            marginRight: '14px',
            background: `linear-gradient(180deg, ${isUser ? '#6366F1' : '#8B5CF6'} 0%, ${isUser ? '#6366F1' : '#8B5CF6'}88 100%)`
          }} />

          {/* Icon + text */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${isUser ? '#6366F1' : '#8B5CF6'}22 0%, ${isUser ? '#6366F1' : '#8B5CF6'}44 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              border: `1px solid ${isUser ? '#6366F1' : '#8B5CF6'}33`,
            }}>
              {isUser ? "👤" : "👥"}
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 className="twl-title" style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '700',
                color: '#1E293B',
                lineHeight: '1.2',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {isUser ? "Data Owner Work Load" : "Auditor Work Load"}
              </h3>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: '#64748B',
                marginTop: '2px',
                lineHeight: '1.4',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {isUser ? "Data entry progress per data owner" : "Verification and approval status per auditor"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {filteredData.length > 0 ? (
        <>
          <div className="twl-table-wrapper">
            <Table className="twl-table">
              <thead>
                <tr>
                  <th className="twl-th name-column">Name</th>
                  <th className="twl-th progress-column">Progress</th>
                  {!isUser && <th className="twl-th count-column">Total</th>}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.userId} className="twl-row">
                    {/* Name cell */}
                    <td className="twl-td name-column">
                      <span className="twl-name">
                        {item.firstName} {item.lastName}
                      </span>
                    </td>

                    {/* Progress bar cell */}
                    <td className="twl-td progress-column">
                      <div
                        className="twl-bar-wrapper"
                        onMouseEnter={() => !isUser && setHoveredUserId(item.userId)}
                        onMouseLeave={() => !isUser && setHoveredUserId(null)}
                      >
                        <div className="bar-container">
                          {isUser
                            ? renderUserBars(item, activeLegend)
                            : renderAuditBars(item, activeLegend)}
                        </div>
                        {/* Tooltip — audit mode only */}
                        {!isUser && hoveredUserId === item.userId && (
                          <AuditTooltip item={item} />
                        )}
                      </div>
                    </td>

                    {/* Total badge — audit mode only */}
                    {!isUser && (
                      <td className="twl-td count-column">
                        <span className="twl-total-badge">
                          {Number(item?.totalAssignedQuestionForAudit ?? 0)}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Legend */}
          <div className="legend">
            {[
              { key: "Approved", label: "Approved", cls: "accepted" },
              { key: "rejected", label: "Rejected", cls: "rejected" },
              { key: "not-responded", label: isUser ? "Not Responded" : "Pending", cls: "not-responded" },
            ].map(({ key, label, cls }) => (
              <div
                key={key}
                className={`legend-item ${activeLegend.includes(key) ? "active" : "inactive"}`}
                onClick={() => handleLegendClick(key)}
                style={{ cursor: "pointer" }}
              >
                <div className={`legend-color ${cls}`} />
                <div className="legend-text">{label}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="twl-empty">
          <img src={no} height="150px" width="150px" alt="No data" />
          <p className="twl-empty-text">No data available</p>
        </div>
      )}
    </div>
  );
};

export default TeamWorkLoad;
